import React, { useEffect, useMemo, useState } from 'react'
import { Stack } from '@sureco/design-system'
import { Button, Input, Typography, message } from 'antd'

const { Title, Text } = Typography

type BrainSharkSectionProps = {
  year: number
  getBrainSharkUrlForYear: (year: number) => string
  saveBrainSharkUrl: (year: number, url: string) => Promise<void>
}

export default function BrainSharkSection({
  year,
  getBrainSharkUrlForYear,
  saveBrainSharkUrl,
}: BrainSharkSectionProps) {
  const initialUrlFromServer = useMemo(
    () => getBrainSharkUrlForYear(year),
    [getBrainSharkUrlForYear, year]
  )

  const [isEditing, setIsEditing] = useState<boolean>(!initialUrlFromServer)
  const [value, setValue] = useState<string>(initialUrlFromServer ?? '')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setValue(initialUrlFromServer ?? '')
    setIsEditing(!initialUrlFromServer)
  }, [initialUrlFromServer, year])

  const trimmed = value.trim()
  const hasValue = trimmed.length > 0

  const handleSave = async (): Promise<void> => {
    if (!hasValue) return

    try {
      setIsSaving(true)
      await saveBrainSharkUrl(year, trimmed)
      message.success('BrainShark URL saved.')
      setIsEditing(false)
    } catch (err) {
      console.error('[Education] Failed to save BrainShark URL', err)
      message.error('Failed to save BrainShark URL. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = (): void => {
    setIsEditing(true)
  }

  return (
    <div style={{ flex: 1 }}>
      <Stack space={12}>
        {/* Row: label on the left, button on the right */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={5} style={{ marginBottom: 0 }}>
            Brain Shark URL
          </Title>

          {isEditing ? (
            hasValue && (
              <Button type="primary" onClick={handleSave} loading={isSaving}>
                Save
              </Button>
            )
          ) : (
            <Button type="primary" onClick={handleEdit}>
              Edit
            </Button>
          )}
        </div>

        <Text type="secondary">
          Add URL to online benefits education like BrainShark.
        </Text>

        {isEditing ? (
          <Input
            placeholder="http://"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          initialUrlFromServer && (
            <a
              href={initialUrlFromServer}
              target="_blank"
              rel="noopener noreferrer"
            >
              {initialUrlFromServer}
            </a>
          )
        )}
      </Stack>
    </div>
  )
}
